import { zodResolver } from "@hookform/resolvers/zod";
import { ComponentProps, useState } from "react";
import { useForm } from "react-hook-form";
import z from "zod";
import { enqueueSnackbar } from "../../../../../components/GlobalSnackbar/snackbarStore";
import { isSupabaseError } from "../../../../../constants/supabaseErrors";
import AddOtherActionModal from "./AddOtherActionModal";
import useSubjectActionModal from "../useSubjectActionModal";
import { formSchema as formSchemaBase } from "../types";
import emptyHtmlToNull from "../../../../../utils/emptyHtmlToNull";

const formSchema = formSchemaBase.extend({
  title: z.string().trim().min(1, "Ce champ est obligatoire"),
  comment: z.string().trim().optional().transform(emptyHtmlToNull),
})

type FormInputs = z.input<typeof formSchema>;
type FormValues = z.infer<typeof formSchema>;

export default function useAddOtherActionModal(props: ComponentProps<typeof AddOtherActionModal>) {
  const [serverError, setServerError] = useState<string | null>(null);

  const [showComment, setShowComment] = useState(!!props.item?.comment);
  const [showProvider, setShowProvider] = useState(!!props.item?.provider_name);
  const [showContact, setShowContact] = useState(!!props.item?.contact_name);
  const [showDocuments, setShowDocuments] = useState(false);

  const form = useForm<FormInputs, unknown, FormValues>({
    mode: "onChange",
    defaultValues: {
      date: props.item?.date ? new Date(props.item.date) : new Date(),
      title: props.item?.title || "",
      comment: props.item?.comment || "",
      provider_name: props.item?.provider_name || "",
      contact_name: props.item?.contact_name || "",
      documents: [],
    },
    resolver: zodResolver(formSchema),
  });

  const {
    isDocumentLoading,
    isLoading,
    handleCallApi,
  } = useSubjectActionModal(
    "other",
    props.item?.id ?? undefined,
    props.subjectId,
    form,
  );

  async function handleConfirm(data: FormValues) {
    setServerError(null);

    try {
      await handleCallApi(data);

      enqueueSnackbar(props.item ? "Action mise à jour." : "Action enregistrée.", "success");
      props.onClose();
    } catch (error: unknown) {
      if (isSupabaseError(error)) {
        setServerError(error.message);
        return;
      }

      setServerError(
        "Une erreur est survenue",
      );
    }
  }

  const documents = form.watch("documents");

  return {
    form,
    handleConfirm,
    isLoading,
    serverError,
    isDocumentLoading,
    showComment,
    showProvider,
    showContact,
    showDocuments: showDocuments || documents.length > 0,
    revealComment: () => setShowComment(true),
    revealProvider: () => setShowProvider(true),
    revealContact: () => setShowContact(true),
    revealDocuments: () => setShowDocuments(true),
  };
}
